import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscription, interval } from 'rxjs';

import { environment } from '@src/environments/environment';
import { ThemeService } from '@app/services';
import { WebSocketService } from '@app/services/websocket/WebSocketService';
import { User } from '@app/models/backend/user';

/** Marcador del ítem que abre el chat corporativo (Zulip) con login automático. */
const CHAT_ACTION = 'action:chat';


/**
 * Menú lateral responsive generado dinámicamente con Tailwind.
 * Incluye TODOS los roles del sistema.
 * Sin archivos SCSS: solo utilidades Tailwind.
 */
@Component({
  selector: 'app-menu-list',
  templateUrl: './menu-list.component.html',
  styles: [
    `
      :host {
        display: block;
      }

      .active-menu-item {
        background-color: #A501041A !important;
        color: #A50104 !important;
      }

      .active-menu-item mat-icon {
        color: #A50104 !important;
      }
    `,
  ],
})
export class MenuListComponent implements OnInit, OnDestroy {
  // ============ Logo Animation ============
  isFlipped = false;
  private flipSubscription?: Subscription;

  // ============ Inputs / Outputs ============
  @Input() isAuthorized: boolean | null = null;
  private _user: User | null = null;
  @Input() set user(val: User | null) {
    this._user = val;
    this.visibleSections = this.buildMenu();
  }
  get user() {
    return this._user;
  }

  @Output() signOut = new EventEmitter<void>();
  @Output() closeMenuEvent = new EventEmitter<void>();

  // ============ State ============
  isDarkTheme = false;
  visibleSections: Section[] = [];
  private expanded = new Set<string>();
  private sub = new Subscription();
  private signingOut = false;
  isMobile = false;

  constructor(
    private router: Router,
    private theme: ThemeService,
    private ws: WebSocketService,
    private http: HttpClient
  ) { }

  // ============ Lifecycle ============
  ngOnInit(): void {
    this.sub.add(this.theme.darkMode$.subscribe((d) => (this.isDarkTheme = d)));
    this.visibleSections = this.buildMenu();
    this.checkIfMobile();

    // Iniciar la animación automática del logo cada 3 segundos
    this.flipSubscription = interval(3000).subscribe(() => {
      this.isFlipped = !this.isFlipped;
    });

    // Escuchar cambios de tamaño de ventana
    window.addEventListener('resize', () => this.checkIfMobile());
  }
  ngOnDestroy(): void {
    this.sub.unsubscribe();
    window.removeEventListener('resize', () => this.checkIfMobile());

    // Limpiar la suscripción del logo
    if (this.flipSubscription) {
      this.flipSubscription.unsubscribe();
    }
  }

  // ============ UI helpers ============
  toggle(item: MenuItem) {
    this.expanded.has(item.label)
      ? this.expanded.delete(item.label)
      : this.expanded.add(item.label);
  }
  isOpen(item: MenuItem) {
    return this.expanded.has(item.label);
  }
  isExternalRoute(route: string | undefined): boolean {
    return !!route && /^https?:\/\//.test(route);
  }
  closeMenu() {
    this.closeMenuEvent.emit();
  }

  /**
   * Navega a la ruta especificada y cierra el menú
   * Evita problemas de doble click en la navegación
   */
  getDynamicUrl(route: string | undefined): string {
    if (!route) return '';
    if (route.includes('educate.smartglobalsales.com')) {
      const miToken = localStorage.getItem('token') || '';
      return `https://educate.smartglobalsales.com/sso?token=${miToken}`;
    }
    return route;
  }

  /**
   * Abre el chat (Zulip) con la sesión del CRM: envía el token del CRM por POST a
   * /accounts/login/crm/, donde Zulip lo valida contra el backend. Se usa un
   * formulario con target=_blank para que el token no viaje en la URL.
   */
  abrirChat(): void {
    const token = localStorage.getItem('token') || '';
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${environment.urlChat}accounts/login/crm/`;
    form.target = '_blank';
    form.style.display = 'none';
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'token';
    input.value = token;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    form.remove();
    this.closeMenu();
  }

  navigateAndClose(route: string | undefined): void {
    if (!route) return;

    if (route === CHAT_ACTION) {
      this.abrirChat();
      return;
    }

    if (/^https?:\/\//.test(route)) {
      const finalUrl = this.getDynamicUrl(route);
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
      this.closeMenu();
      return;
    }

    // Prevenir navegación múltiple
    if (this.router.url === route) {
      this.closeMenu();
      return;
    }

    // Navegar y cerrar menú
    this.router
      .navigate([route])
      .then(() => {
        this.closeMenu();
      })
      .catch((error) => {
        console.error('Error en navegación:', error);
        this.closeMenu();
      });
  }

  openExternalAndClose(url: string | undefined): void {
    if (!url) return;

    window.open(url, '_blank', 'noopener,noreferrer');
    this.closeMenu();
  }

  /**
   * Detecta si estamos en un dispositivo móvil
   */
  private checkIfMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  // Verifica si un elemento padre debe estar activo basado en sus hijos
  isParentActive(item: MenuItem): boolean {
    if (!item.children) return false;

    const currentUrl = this.router.url;
    return item.children.some((child) => child.route === currentUrl);
  }

  /**
   * Cierra la sesión del usuario de forma completa
   */
  onSignOut(): void {
    // Prevenir múltiples clicks durante el proceso de logout
    if (this.signingOut) return;
    this.signingOut = true;

    const token = localStorage.getItem('token');
    const userId = this.user?.id;

    if (token && userId) {
      // Hacer logout en el servidor
      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
      this.http
        .post(
          `${environment.url}api/authentication/sign-out`,
          { userId },
          { headers }
        )
        .subscribe({
          next: () => {
            console.log('Logout exitoso en el servidor');
            // Solo emitir el evento, el app.component se encarga del resto
            this.signOut.emit();
            this.signingOut = false;
          },
          error: (error) => {
            console.error('Error en logout del servidor:', error);
            // Aunque falle el logout del servidor, emitir el evento para logout local
            this.signOut.emit();
            this.signingOut = false;
          },
        });
    } else {
      // Si no hay token, hacer logout local directamente
      console.log('No hay token, haciendo logout local');
      this.signOut.emit();
      this.signingOut = false;
    }
  }

  // ============ Generador de menú ============
  private buildMenu(): Section[] {
    const role = this.user?.role || '';
    const tipoTrabajo = this.user?.tipoTrabajo || '';
    const username = this.user?.username || '';
    const sections: Section[] = [];
    const add = (label: string, items: MenuItem[]) =>
      sections.push({ label, items });

    // Panel base (excluir COACHING, EVENTO y SEGURIDAD)
    if (role !== 'COACHING' && role !== 'EVENTO' && role !== 'SEGURIDAD') {
      // El ítem "Inicio" ahora está directamente en el HTML en la sección roja superior
    }
    const educationCommon: MenuItem[] = [
      { label: 'Mis Cursos', icon: 'school', route: '/cursos/mis-cursos' },
      {
        label: 'EDUCATE',
        icon: 'cast_for_education',
        route: 'https://educate.smartglobalsales.com',
      },
    ];

    switch (role) {
      case 'ADMIN':
      case 'LECTOR':
      case 'PROGRAMADOR':
      case 'MARKETING':
      case 'SUPERADMIN': {
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
          {
            label: 'Leads',
            icon: 'assignment',
            route: '/leads/listar',
          },
          { label: 'Anuncios', icon: 'campaign', route: '/anuncios/list' },
          { label: 'Ventas', icon: 'shopping_cart', route: '/ventas' },
          { label: 'Foro', icon: 'question_answer', route: '/faq' },
          { label: 'Archivos', icon: 'folder', route: '/archivos' },
          // { label: 'Evento QR', icon: 'qr_code_scanner', route: '/evento-qr' },
        ]);

        // Construir items administrativos base
        const administrativoItems: MenuItem[] = [
          {
            label: 'Supervisión',
            icon: 'person_add',
            route: '/coordinador/listar',
          },
          { label: 'Usuarios', icon: 'group', route: '/auth/listar' },
          {
            label: 'IPs Permitidas',
            icon: 'security',
            route: '/ipsPermitidas',
          },
          {
            label: 'Promociones',
            icon: 'redeem',
            route: '/promociones',
          },
          {
            label: 'Notificaciones',
            icon: 'notifications_active',
            route: '/notificaciones',
          },
          { label: 'Sede', icon: 'location_on', route: '/sede' },
          {
            label: 'Reporteria Conserjes',
            icon: 'schedule',
            route: '/horario-conserjes',
          },
          {
            label: 'Datos Landing',
            icon: 'folder_open',
            children: [
              {
                label: 'Contacto',
                icon: 'contact_mail',
                route: '/landing-contact',
              },
              {
                label: 'Postulación',
                icon: 'how_to_reg',
                route: '/postulacion',
              },
              {
                label: 'Clientes SMS',
                icon: 'sms',
                route: '/clientes-sms',
              },
            ],
          },
          {
            label: 'Análisis Transcripción',
            icon: 'analytics',
            route: '/analisis-transcripcion',
          },
          {
            label: 'Excel Tipificaciones',
            icon: 'table_view',
            route: '/excel-tipificaciones',
          },
        ];

        // START: Agregado Panel Auditoría Logins
        if (['ADMIN', 'PROGRAMADOR', 'SUPERADMIN'].includes(role)) {
          administrativoItems.push({
            label: 'Logs generales',
            icon: 'policy',
            route: '/logs-generales',
          });
        }
        // END: Agregado Panel Auditoría Logins

        // Agregar Logs Ventas solo para usuarios específicos
        const logsVentasUsernames = ['72640568', '73635487', 'josetorchi'];
        if (logsVentasUsernames.includes(username)) {
          administrativoItems.push({
            label: 'Logs Ventas',
            icon: 'analytics',
            route: '/logs-ventas',
          });
        }

        add('Administrativo', administrativoItems);

        add('Educación', [
          { label: 'Administrar Cursos', icon: 'school', route: '/cursos' },
          {
            label: 'Gestión de Certificados',
            icon: 'workspace_premium',
            route: '/certificados',
          },
          ...educationCommon,
        ]);

        add('Herramientas', [
          { label: 'Mapa', icon: 'map', route: '/map-example' },
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },

              {
                label: 'Asignación de Tareas',
                icon: 'task_alt',
                route: '/tareas_v2',
              },
              {
                label: 'Encuestas',
                icon: 'assignment',
                route: '/encuestas',
              },
              {
                label: 'Coaching',
                icon: 'emoji_people',
                route: '/coaching',
              },
            ],
          },
        ]);
        add('Dashboards', [
          {
            label: 'Dashboard Supervisor',
            icon: 'dashboard',
            route: '/dashboard-supervisor',
          },
          {
            label: 'Dashboard Comercial',
            icon: 'dashboard',
            route: '/dashboard-comercial',
          },
          {
            label: 'Dashboard Backoffice',
            icon: 'dashboard',
            route: '/dashboard-backoffice',
          },
          {
            label: 'Reporte General',
            icon: 'assessment',
            route: '/dashboard-reporte-general',
          },
        ]);
        break;
      }

      case 'JEFEPISO': {
        // JEFEPISO solo puede ver: HOME, MANUALES, VENTAS, CURSOS, COACHING
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Ventas', icon: 'shopping_cart', route: '/ventas' },
        ]);

        add('Educación', educationCommon);
        add('Herramientas', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);

        add('Educación', educationCommon);

        break;
      }

      case 'AUDITOR': {
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
          {
            label: 'Listar Clientes',
            icon: 'how_to_reg',
            route: '/leads/listar',
          },
          { label: 'Foro', icon: 'question_answer', route: '/faq' },
          { label: 'Encuestas', icon: 'quiz', route: '/encuestas' },
        ]);
        add('Educación', educationCommon);
        add('Herramientas', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);
        break;
      }

      case 'COACHING': {
        add('Aplicaciones', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Asignación de Tareas',
                icon: 'task_alt',
                route: '/tareas_v2',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
              {
                label: 'Coaching',
                icon: 'emoji_people',
                route: '/coaching',
              },
            ],
          },
        ]);

        break;
      }

      case 'BACKOFFICE': {
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
          {
            label: 'Listar Clientes',
            icon: 'group',
            route: '/leads/listar',
          },
          { label: 'Ventas', icon: 'shopping_cart', route: '/ventas' },
          { label: 'Foro', icon: 'question_answer', route: '/faq' },
          { label: 'Encuestas', icon: 'quiz', route: '/encuestas' },
          { label: 'Archivos', icon: 'folder', route: '/archivos' },
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);
        add('Educación', educationCommon);
        add('Herramientas', [
          { label: 'Mapa', icon: 'map', route: '/map-example' },
        ]);
        add('Dashboards', [
          {
            label: 'Dashboard Personal Backoffice',
            icon: 'dashboard',
            route: '/dashboard-backoffice',
          },
        ]);
        break;
      }

      case 'BACKOFFICETRAMITADOR':
      case 'BACKOFFICESEGUIMIENTO':
      case 'ASESORYOIGO': {
        // 1. Define los items base para todos los usuarios con este rol
        const aplicacionesItems: MenuItem[] = [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
          {
            label: 'Leads',
            icon: 'group',
            route: '/leads/listar',
          },
          { label: 'Ventas', icon: 'shopping_cart', route: '/ventas' },
          { label: 'Foro', icon: 'question_answer', route: '/faq' },
          { label: 'Encuestas', icon: 'quiz', route: '/encuestas' },
        ];

        // 2. Condición para añadir la ruta solo al usuario específico
        //    Utilizamos el DNI de la imagen: 76747231
        //    NOTA: Asegúrate de que `this.user.documento` sea la propiedad correcta en tu modelo de usuario (puede ser `id`, `dni`, `username`, etc.).
        if (this.user && this.user.username === '76747231') {
          aplicacionesItems.push({
            label: 'Análisis Transcripción',
            icon: 'analytics',
            route: '/analisis-transcripcion',
          });
        }

        // 3. Añade la lista final de items a la sección del menú
        add('Aplicaciones', aplicacionesItems);
        add('Educación', educationCommon);
        add('Herramientas', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);
        if (role === 'BACKOFFICETRAMITADOR' || role === 'BACKOFFICESEGUIMIENTO') {
          add('Dashboards', [
            {
              label: 'Dashboard Personal Backoffice',
              icon: 'dashboard',
              route: '/dashboard-backoffice-ind',
            },
          ]);
        }
        break;
      }
      case 'COORDINADOR': {
        const aplicacionesCoordinador: MenuItem[] = [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
          {
            label: 'Leads',
            icon: 'assignment',
            route: '/coordinador/obtenerclientesdeasesor',
          },
          { label: 'Ventas', icon: 'shopping_cart', route: '/ventas' },
          { label: 'Foro', icon: 'question_answer', route: '/faq' },
          { label: 'Encuestas', icon: 'quiz', route: '/encuestas' },
          {
            label: 'Turnos Excel',
            icon: 'table_chart',
            route: '/excel-module'
          }
        ];

        if (environment.tipificacionesMetricasEnabled) {
          aplicacionesCoordinador.push({
            label: 'Métricas de bases',
            icon: 'monitoring',
            route: '/excel-tipificaciones/metricas-bases',
          });
        }

        add('Aplicaciones', aplicacionesCoordinador);
        add('Educación', educationCommon);
        add('Herramientas', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },

            ],
          },
        ]);
        break;
      }

      case 'ASESOR': {
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
          { label: 'Ventas', icon: 'shopping_cart', route: '/ventas' },
          { label: 'Foro', icon: 'question_answer', route: '/faq' },
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);
        add('Educación', educationCommon);
        break;
      }

      case 'PSICOLOGO': {
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
          { label: 'Encuestas', icon: 'quiz', route: '/encuestas' },
        ]);
        add('Educación', educationCommon);
        add('Herramientas', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);
        break;
      }

      case 'GERENCIA': {
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Agenda', icon: 'calendar_today', route: '/calendar' },
          { label: 'Anuncios', icon: 'campaign', route: '/anuncios/list' },
          { label: 'Foro', icon: 'question_answer', route: '/faq' },
          { label: 'Encuestas', icon: 'quiz', route: '/encuestas' },
        ]);
        add('Administrativo', [
          {
            label: 'Reporteria Conserjes',
            icon: 'schedule',
            route: '/horario-conserjes',
          }
        ]);
        add('Educación', educationCommon);
        add('Herramientas', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);
        break;
      }

      case 'RECURSOSHUMANOS': {
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' },
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
          // Se asume que la ruta para Asistencia es '/asistencias'
          {
            label: 'Asistencia',
            icon: 'checklist',
            route: '/asistencias/admin',
          },
        ]);

        add('Administrativo', [
          { label: 'Usuarios', icon: 'group', route: '/auth/listar' },
        ]);

        add('Educación', educationCommon); // Esto añade "Mis Cursos"

        add('Herramientas', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuentas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);
        break;
      }

      case 'SEGURIDAD': {
        add('Aplicaciones', [
          { label: 'Asistencias', icon: 'checklist', route: '/asistencias' },
          // { label: 'Evento QR', icon: 'qr_code_scanner', route: '/evento-qr' },
        ]);
        break;
      }

      case 'EVENTO': {
        add('Aplicaciones', [
          // { label: 'Evento QR', icon: 'qr_code_scanner', route: '/evento-qr' },
        ]);
        break;
      }

      case 'MASAJISTA':
      case 'CONSERJE':
      case 'CAPACITADOR': {
        add('Aplicaciones', [
          { label: 'Manuales', icon: 'menu_book', route: '/manual' }, // ✅ Acceso a Manuales para Capacitador
          { label: 'Calendario', icon: 'calendar_today', route: '/calendar' },
        ]);

        // Agregar Reporteria Conserjes solo para CONSERJE
        if (role === 'CONSERJE') {
          add('Administrativo', [
            {
              label: 'Reporteria Conserjes',
              icon: 'schedule',
              route: '/horario-conserjes',
            }
          ]);
        }
        add('Educación', educationCommon);

        add('Herramientas', [
          {
            label: 'Coaching',
            icon: 'folder_open',
            children: [
              {
                label: 'Panel Coaching',
                icon: 'psychology',
                route: '/coaching-dashboard',
              },
              {
                label: 'Tareas',
                icon: 'task_alt',
                route: '/tareas_v2/tarea-asesor',
              },
              {
                label: 'Encuestas',
                icon: 'assignment',
                route: '/encuestas',
              },
            ],
          },
        ]);
        break;
      }

      default: {
        // Roles desconocidos -> solo panel principal y cursos
        add('Educación', educationCommon);
        break;
      }
    }

    // Módulo de Hojas de Cálculo — accesible para todos los usuarios autenticados
    if (role) {
      // Verificar si ya existe una sección de Herramientas, si no crearla
      const spreadsheetItem = { label: 'EXCEL', icon: 'table_view', route: '/spreadsheet' };
      const herramientas = sections.find(s => s.label === 'Herramientas');
      
      if (herramientas) {
        // Solo agregar si no existe ya
        if (!herramientas.items.some(i => i.route === '/spreadsheet')) {
          herramientas.items.push(spreadsheetItem);
        }
      } else {
        add('Herramientas', [spreadsheetItem]);
      }

      // El chat corporativo está disponible para todos los roles con sesión.
      add('Comunicación', [
        {
          label: 'Chat Corporativo',
          icon: 'chat',
          route: CHAT_ACTION,
        },
      ]);
    }

    // ============ Módulo especial para usuarios con tipoTrabajo = "Excel" ============
    if (tipoTrabajo === 'Excel') {
      add('Cola Llamadas', [
        {
          label: 'Turnos Excel',
          icon: 'table_chart',
          route: '/excel-module'
        }
      ]);
    }

    return sections;
  }
}

// ===== Interfaces helper =====
interface Section {
  label: string;
  items: MenuItem[];
}

interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  externalUrl?: string;
  children?: MenuItem[];
}
